import { createTheme, Theme, alpha } from '@mui/material/styles';
import { createAppTheme } from './createAppTheme';
import type { ColorSchemeDefinition } from './colorSchemes';

export const createAdminTheme = (
    scheme: ColorSchemeDefinition,
    isDark: boolean
): Theme => {
    const baseTheme = createAppTheme(scheme, isDark);
    const primary = baseTheme.palette.primary.main;

    // Border color — clean, subtle dividers (Vercel/Stripe style)
    const border = alpha(isDark ? '#fff' : '#000', isDark ? 0.1 : 0.08);
    const borderSubtle = alpha(isDark ? '#fff' : '#000', isDark ? 0.06 : 0.05);

    return createTheme(baseTheme, {
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiPaper: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundImage: 'none',
                        overflow: 'auto',
                    },
                    outlined: {
                        border: `1px solid ${border}`,
                    },
                    elevation0: {
                        boxShadow: 'none',
                    },
                },
            },
            MuiCard: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        border: `1px solid ${border}`,
                        boxShadow: 'none',
                        transition: 'border-color 0.15s ease',
                        '&:hover': {
                            borderColor: alpha(primary, 0.3),
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        textTransform: 'none' as const,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        padding: '6px 16px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    },
                    outlined: {
                        borderColor: border,
                    },
                    sizeSmall: {
                        borderRadius: 6,
                        padding: '4px 12px',
                        fontSize: '0.8125rem',
                    },
                    sizeLarge: {
                        borderRadius: 8,
                        padding: '10px 24px',
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: border,
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 6,
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                        border: `1px solid ${border}`,
                        boxShadow: `0 16px 48px ${alpha('#000', isDark ? 0.4 : 0.12)}`,
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontSize: '1.125rem',
                        fontWeight: 600,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                    },
                    sizeSmall: {
                        borderRadius: 4,
                        fontSize: '0.75rem',
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        border: `1px solid ${border}`,
                    },
                    standardSuccess: {
                        backgroundColor: alpha('#4caf50', isDark ? 0.1 : 0.05),
                        borderColor: alpha('#4caf50', 0.2),
                    },
                    standardError: {
                        backgroundColor: alpha('#f44336', isDark ? 0.1 : 0.05),
                        borderColor: alpha('#f44336', 0.2),
                    },
                    standardWarning: {
                        backgroundColor: alpha('#ff9800', isDark ? 0.1 : 0.05),
                        borderColor: alpha('#ff9800', 0.2),
                    },
                    standardInfo: {
                        backgroundColor: alpha('#2196f3', isDark ? 0.1 : 0.05),
                        borderColor: alpha('#2196f3', 0.2),
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none' as const,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        minHeight: 40,
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        height: 2,
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        border: `1px solid ${border}`,
                        overflow: 'auto',
                        '& .MuiDataGrid-main': {
                            overflow: 'auto',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: isDark
                                ? alpha('#fff', 0.03)
                                : alpha('#000', 0.02),
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.04em',
                            color: baseTheme.palette.text.secondary,
                        },
                        '& .MuiDataGrid-row': {
                            '&:hover': {
                                backgroundColor: alpha(primary, isDark ? 0.06 : 0.02),
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderColor: borderSubtle,
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: borderSubtle,
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                        color: isDark ? '#ffffff' : baseTheme.palette.text.primary,
                        borderBottom: `1px solid ${border}`,
                        boxShadow: 'none',
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        borderRight: `1px solid ${border}`,
                        backgroundColor: isDark ? '#141414' : '#fafafa',
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        margin: '1px 8px',
                        '&.Mui-selected': {
                            backgroundColor: alpha(primary, isDark ? 0.15 : 0.1),
                            borderLeft: `3px solid ${primary}`,
                            paddingLeft: 'calc(0.5rem - 3px)',
                            '& .MuiListItemIcon-root': {
                                color: primary,
                            },
                            '& .MuiListItemText-primary': {
                                fontWeight: 600,
                            },
                            '&:hover': {
                                backgroundColor: alpha(primary, isDark ? 0.2 : 0.14),
                            },
                        },
                    },
                },
            },
            MuiListSubheader: {
                styleOverrides: {
                    root: {
                        fontSize: '0.6875rem',
                        lineHeight: '28px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase' as const,
                        fontWeight: 600,
                        backgroundColor: 'transparent',
                        color: baseTheme.palette.text.secondary,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: 6,
                        fontSize: '0.8125rem',
                        padding: '4px 10px',
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderColor: borderSubtle,
                    },
                },
            },
            MuiBreadcrumbs: {
                styleOverrides: {
                    root: {
                        fontSize: '0.8125rem',
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        '& .MuiTableCell-head': {
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.04em',
                            color: baseTheme.palette.text.secondary,
                            backgroundColor: isDark
                                ? alpha('#fff', 0.03)
                                : alpha('#000', 0.02),
                        },
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        'tbody &:nth-of-type(even)': {
                            backgroundColor: alpha('#000', isDark ? 0.02 : 0.015),
                        },
                        '&.MuiTableRow-hover:hover': {
                            backgroundColor: alpha(primary, isDark ? 0.06 : 0.03),
                        },
                        '&.Mui-selected': {
                            backgroundColor: alpha(primary, isDark ? 0.1 : 0.06),
                            '&:hover': {
                                backgroundColor: alpha(primary, isDark ? 0.12 : 0.08),
                            },
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: borderSubtle,
                    },
                },
            },
        },
    });
};
